import { Button, Icon, Text } from '@pm/ui';
import { useToggle } from '@pm/util';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  ShipmentLike,
  useShipmentStatus,
} from '../../../hooks/use-shipment-status/useShipmentStatus';
import { labsRoutes } from '../../../modules/labs/labs-routes';
import { formatDateToLongFormYear } from '../../../utilities/dateUtilities';
import { LabKitShipmentModal } from '../LabKitShipmentModal';

type LabResultDownloadProps = {
  documentBasedLabResultId: string;
  orderedAt: string;
  documentUploadedAt?: string | null;
  outboundShipment: ShipmentLike | null;
  inboundShipment: ShipmentLike | null;
  supportLink: string;
};

export const LabResultDownload = ({
  documentBasedLabResultId,
  orderedAt,
  documentUploadedAt,
  outboundShipment,
  inboundShipment,
  supportLink,
}: LabResultDownloadProps) => {
  const { t } = useTranslation('labs');
  const [showTrackingModal, trackingModalHandlers] = useToggle();
  const { status, description, trackingNumber, shouldShowSupportLink } =
    useShipmentStatus({ inboundShipment, outboundShipment });

  const orderedAtFormatted = formatDateToLongFormYear(
    new Date(orderedAt),
    i18n.language,
  );
  const resultsReceivedAt = documentUploadedAt
    ? formatDateToLongFormYear(new Date(documentUploadedAt), i18n.language)
    : null;

  const handleDownload = () => {
    window.open(
      labsRoutes.document.build({ id: documentBasedLabResultId }),
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <div className="flex flex-col gap-s">
      <div className="flex items-start gap-m">
        <img
          src="/images/lab-kit-bg.webp"
          alt=""
          className="w-[56px] h-[56px] shrink-0 rounded-s"
        />
        <div className="flex flex-col gap-xxs">
          <Text size="headingS" as="h2">
            {t('LabResultDownload.Title')}
          </Text>
          <Text size="bodyS" color="contentSubdued">
            {t('LabResultDownload.Ordered', { date: orderedAtFormatted })}
          </Text>
          {resultsReceivedAt && (
            <Text size="bodyS" color="contentPositive">
              {t('LabResultDownload.ResultsReceived', {
                date: resultsReceivedAt,
              })}
            </Text>
          )}
        </div>
      </div>
      <Button
        intent="secondary"
        onClick={handleDownload}
        trailingIcon={<Icon name="download" size="medium" />}
      >
        {t('LabResultDownload.DownloadResults')}
      </Button>
      <Button intent="secondary" onClick={trackingModalHandlers.on}>
        {t('HeroCard.ViewTrackingHistory')}
      </Button>
      <LabKitShipmentModal
        status={status}
        description={description}
        trackingNumber={trackingNumber}
        isOpen={showTrackingModal}
        onClose={trackingModalHandlers.off}
        supportLink={supportLink}
        shouldShowSupportLink={shouldShowSupportLink}
      />
    </div>
  );
};
